// Libs
import moment from "moment";
import { useCallback, useState } from "react";

// Types
import {
    SERVICE_TIME_HOURS,
    TimeSlotSelectFilterChoiceEnum,
    type TimeSlotSelectFilterValue,
} from "src/components-ds/filters/types";
import type { TFunction } from "src/design-system/core/TranslationContext";

// Utils
import {
    FILTERS_COMPONENTS_TRANSLATION_NAMESPACE,
    TIMESLOT_FILTER_TK,
} from "src/components/Filters/config";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";
import { ServiceTime } from "src/types/offerSlot";
import { formatDurationToSlot } from "src/utils/formatters";

const TIME_SLOT_DEFAULT_VALUE = {
    allday: true,
    serviceTime: TimeSlotSelectFilterChoiceEnum.AllDay,
    durationStartFromMidnight: undefined,
    durationEndFromMidnight: undefined,
};

export type UseTimeSlotFilterValue = TimeSlotSelectFilterValue;

export interface UseTimeSlotFilterOptions {
    defaultValue?: UseTimeSlotFilterValue;
    initialValue?: UseTimeSlotFilterValue;
}

export interface UseTimeSlotFilterResult {
    dirtyTimeSlot: UseTimeSlotFilterValue;
    handleTimeSlotChange: (timeSlot: UseTimeSlotFilterValue) => void;
    isEmpty: boolean;
    resetFilter: () => void;
    softResetFilter: () => void;
    timeSlot: UseTimeSlotFilterValue;
}

/**
 * Hook to handle a TimeSlot filter
 * @param options Hooks options
 * @returns Helpers to handle TimeSlot filter
 */
export function useTimeSlotFilter({
    defaultValue = TIME_SLOT_DEFAULT_VALUE,
    initialValue,
}: UseTimeSlotFilterOptions = {}): UseTimeSlotFilterResult {
    const [timeSlot, setTimeSlot] = useState<UseTimeSlotFilterValue>(initialValue ?? defaultValue);
    const [dirtyTimeSlot, setDirtyTimeSlot] = useState<UseTimeSlotFilterValue>(timeSlot);

    const handleTimeSlotChange = useCallback((newTimeSlot: UseTimeSlotFilterValue) => {
        setDirtyTimeSlot((currentTimeSlot) => {
            const newValue = { ...currentTimeSlot, ...newTimeSlot };

            setTimeSlot(newValue);
            return newValue;
        });
    }, []);

    const resetFilter = useCallback(
        () => handleTimeSlotChange(defaultValue),
        [handleTimeSlotChange, defaultValue]
    );

    const softResetFilter = useCallback(
        () =>
            !dirtyTimeSlot.serviceTime &&
            !dirtyTimeSlot.durationStartFromMidnight &&
            !dirtyTimeSlot.durationEndFromMidnight &&
            resetFilter(),
        [resetFilter, dirtyTimeSlot]
    );

    return {
        dirtyTimeSlot,
        handleTimeSlotChange,
        isEmpty: isTimeSlotFilterEmpty(timeSlot),
        resetFilter,
        softResetFilter,
        timeSlot,
    };
}

/**
 * Format TimeSlot filter value
 * @param filterValue
 * @param t
 * @returns
 */
export function formatTimeSlotFilter(
    filterValue: UseTimeSlotFilterValue = TIME_SLOT_DEFAULT_VALUE,
    t: TFunction
): string {
    const { serviceTime, durationEndFromMidnight, durationStartFromMidnight } = filterValue;

    if (!serviceTime) {
        return t(`${FILTERS_COMPONENTS_TRANSLATION_NAMESPACE}:${TIMESLOT_FILTER_TK}.allDay`);
    }

    if (serviceTime !== TimeSlotSelectFilterChoiceEnum.Custom) {
        return t(
            `${FILTERS_COMPONENTS_TRANSLATION_NAMESPACE}:${TIMESLOT_FILTER_TK}.${serviceTime}`
        );
    }

    return formatDurationToSlot(durationStartFromMidnight, durationEndFromMidnight, (string) =>
        t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`)
    );
}

/**
 * Compute if filter is empty or not
 * @param filter Filter value
 * @returns If is empty
 */
export function isTimeSlotFilterEmpty(
    filter: UseTimeSlotFilterValue = TIME_SLOT_DEFAULT_VALUE
): boolean {
    return (
        filter.serviceTime === TimeSlotSelectFilterChoiceEnum.AllDay ||
        filter.serviceTime === undefined
    );
}

export function getTimeSlotDurations(timeslot: TimeSlotSelectFilterValue): {
    durations: {
        durationStartFromMidnight: string;
        durationEndFromMidnight: string;
    };
    serviceTime?: ServiceTime;
} {
    switch (timeslot.serviceTime) {
        case TimeSlotSelectFilterChoiceEnum.AllDay:
            return {
                durations: {
                    durationStartFromMidnight: undefined,
                    durationEndFromMidnight: undefined,
                },
            };
        case TimeSlotSelectFilterChoiceEnum.Lunch:
            return {
                durations: {
                    durationStartFromMidnight: moment
                        .duration({ hours: SERVICE_TIME_HOURS.LUNCH.start })
                        .toISOString(),
                    durationEndFromMidnight: moment
                        .duration({ hours: SERVICE_TIME_HOURS.LUNCH.end })
                        .toISOString(),
                },
                serviceTime: ServiceTime.Lunch,
            };
        case TimeSlotSelectFilterChoiceEnum.Dinner:
            return {
                durations: {
                    durationStartFromMidnight: moment
                        .duration({ hours: SERVICE_TIME_HOURS.DINNER.start })
                        .toISOString(),
                    durationEndFromMidnight: moment
                        .duration({ hours: SERVICE_TIME_HOURS.DINNER.end })
                        .toISOString(),
                },
                serviceTime: ServiceTime.Dinner,
            };
        default:
            return {
                durations: {
                    durationStartFromMidnight: timeslot.durationStartFromMidnight?.toISOString(),
                    durationEndFromMidnight: timeslot.durationEndFromMidnight?.toISOString(),
                },
            };
    }
}
